import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Paper,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import DashboardIcon from '@mui/icons-material/Dashboard';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Header from '../../Components/Header/Header';
import TimelineBoard from './TimelineBoard';
import ReservationDashboard from './ReservationDashboard';
import {
  buildSampleDetail,
  buildSampleList,
  buildSampleTimeline,
  SampleChip,
  SampleReservationDetailDialog,
} from './sampleReservation';
import { AppDispatch, RootState } from '../../Redux/Store';
import { fetchReservations, fetchTimeline } from '../../Redux/Reservation/reservationApi';
import {
  RESERVATION_STATUS_COLOR,
  RESERVATION_STATUS_LABEL,
  ReservationListItem,
  ReservationStatus,
  formatReservationDateTime,
  toDateString,
} from '../../types/reservation';

/** 匿名化済み予約の一覧氏名欄の表示（Wave B。氏名は復元不可のダミー値のため実データを出さない） */
const ANONYMIZED_NAME_LABEL = '匿名化済みのお客様';

/** ステータスフィルタの並び。REQUESTED（要対応）を先頭に置く */
const STATUS_FILTERS: ReservationStatus[] = [
  'REQUESTED',
  'CONFIRMED',
  'COMPLETED',
  'CANCELLED',
  'REJECTED',
  'NO_SHOW',
];

/**
 * 予約管理（一覧⇔台帳）。要件: 当日デフォルト・日付ナビ・ステータスフィルタ・検索・
 * REQUESTED強調・手動更新のみ（ポーリング/SSEなし）。
 * サブスク切れでも既存予約の処理ができる必要があるためサブスクゲートは掛けない。
 */
const ReservationManagement: React.FC = () => {
  const { store_id } = useParams<{ store_id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { list, listLoading, listError, timeline, timelineLoading, timelineError } = useSelector(
    (state: RootState) => state.reservation,
  );

  // 一覧の絞り込み状態は URL クエリに保持する（Wave A・7）。
  // 予約詳細から戻ってきてもステータスフィルタ・選択日付・期間・検索が復元される。
  const [searchParams, setSearchParams] = useSearchParams();
  const isReservationStatus = (v: string | null): v is ReservationStatus =>
    v != null && STATUS_FILTERS.includes(v as ReservationStatus);

  const [date, setDate] = useState<string>(
    () => searchParams.get('date') || toDateString(new Date()),
  );
  const [statusFilter, setStatusFilter] = useState<ReservationStatus | null>(() => {
    const s = searchParams.get('status');
    return isReservationStatus(s) ? s : null;
  });
  const [searchInput, setSearchInput] = useState(() => searchParams.get('q') || '');
  const [query, setQuery] = useState(() => searchParams.get('q') || '');
  // リスト / 台帳(timeline) / ダッシュボード(dashboard) の3値を排他表示する（統合契約 タスク2）。
  // ダッシュボードは埋め込みをやめ、同列のタブとして選択したときに単独表示する。
  const [view, setView] = useState<'list' | 'timeline' | 'dashboard'>(() => {
    const v = searchParams.get('view');
    return v === 'timeline' || v === 'dashboard' ? v : 'list';
  });
  // 表示期間: day=選択日のみ / upcoming=今日以降すべて / past=過去14日（台帳は日単位のため upcoming/past ではリスト固定）
  // past は忘れ物等の連絡対応用。個人情報保護のため BE 側で from を now-14日にクランプする（14日より前は表示されない）。
  const [range, setRange] = useState<'day' | 'upcoming' | 'past'>(() => {
    const r = searchParams.get('range');
    return r === 'upcoming' || r === 'past' ? r : 'day';
  });
  // コース別フィルタ（今後すべて表示時のみ）。選択肢は取得済み予約のスナップショット値から生成する
  // （コースは予約時点の名前・価格が焼き込まれるため、現在のコースマスタとは突き合わせない）。
  const [courseFilter, setCourseFilter] = useState<string | null>(
    () => searchParams.get('course') || null,
  );
  // 空状態のときだけ使う「サンプル予約を表示」（FE ローカルのモックのみ・API は呼ばない）
  const [showSample, setShowSample] = useState(false);
  const [sampleDetailId, setSampleDetailId] = useState<string | null>(null);

  // 一覧の取得。検索クエリ指定時は日付をまたいで全期間検索する（氏名/電話/予約番号での横断検索が目的のため）。
  // 期間別の from/to:
  //  - day: 選択日の 00:00〜23:59
  //  - upcoming: 今日 00:00〜（to なし）
  //  - past: 今日-14日 00:00〜今日 23:59（BE も from を now-14日にクランプする）
  const loadList = useCallback(() => {
    if (!store_id) return;
    const today = new Date();
    const from14 = new Date(today);
    from14.setDate(from14.getDate() - 14);
    let fromParam: string | undefined;
    let toParam: string | undefined;
    if (query) {
      fromParam = undefined;
      toParam = undefined;
    } else if (range === 'upcoming') {
      fromParam = `${toDateString(today)}T00:00:00`;
      toParam = undefined;
    } else if (range === 'past') {
      fromParam = `${toDateString(from14)}T00:00:00`;
      toParam = `${toDateString(today)}T23:59:59`;
    } else {
      fromParam = `${date}T00:00:00`;
      toParam = `${date}T23:59:59`;
    }
    dispatch(
      fetchReservations({
        storeId: store_id,
        status: statusFilter ?? undefined,
        from: fromParam,
        to: toParam,
        q: query || undefined,
      }),
    );
  }, [dispatch, store_id, date, statusFilter, query, range]);

  const loadTimeline = useCallback(() => {
    if (!store_id) return;
    dispatch(fetchTimeline({ storeId: store_id, date }));
  }, [dispatch, store_id, date]);

  // 表示時と条件変更時のみ取得（ポーリングはしない）。
  // ダッシュボードは ReservationDashboard 内で自前のAPIを呼ぶため、ここでは取得しない。
  useEffect(() => {
    if (view === 'list') {
      loadList();
    } else if (view === 'timeline') {
      loadTimeline();
    }
  }, [view, loadList, loadTimeline]);

  // 絞り込み状態を URL クエリへ反映する（Wave A・7・戻り時の復元用）。
  // 既定値は URL に載せずクエリを簡潔に保つ。replace で履歴を汚さない。
  useEffect(() => {
    const params = new URLSearchParams();
    if (range !== 'day') params.set('range', range);
    if (range === 'day' && date !== toDateString(new Date())) params.set('date', date);
    if (statusFilter) params.set('status', statusFilter);
    if (query) params.set('q', query);
    if (view === 'timeline' || view === 'dashboard') params.set('view', view);
    if (range === 'upcoming' && courseFilter) params.set('course', courseFilter);
    setSearchParams(params, { replace: true });
  }, [range, date, statusFilter, query, view, courseFilter, setSearchParams]);

  const moveDate = (deltaDays: number) => {
    const d = new Date(`${date}T00:00:00`);
    d.setDate(d.getDate() + deltaDays);
    setDate(toDateString(d));
  };

  const handleSearch = () => {
    setQuery(searchInput.trim());
  };

  const handleRefresh = () => {
    if (view === 'list') loadList();
    else if (view === 'timeline') loadTimeline();
    // ダッシュボードは内部で anchor/粒度に応じて自前取得するためここでは何もしない
  };

  const openDetail = (reservationId: string) => {
    navigate(`/${store_id}/manage/reservation/${reservationId}`);
  };

  // コースフィルタの選択肢は取得済みデータのスナップショット値（予約時点のコース名）から作る。
  const courseOptions = Array.from(new Set(list.map((r) => r.selectedMenuName ?? ''))).sort();
  const visibleList =
    range === 'upcoming' && courseFilter !== null
      ? list.filter((r) => (r.selectedMenuName ?? '') === courseFilter)
      : list;

  /**
   * 今後すべて/過去表示用: 日付ごとにグループ化して見出しを付ける。
   * desc=true（過去表示）のときは直近が上に来るよう降順、それ以外は昇順。
   */
  const groupByDate = (
    items: ReservationListItem[],
    desc = false,
  ): [string, ReservationListItem[]][] => {
    const map = new Map<string, ReservationListItem[]>();
    items.forEach((r) => {
      const d = r.reservationTime.slice(0, 10);
      const arr = map.get(d) ?? [];
      arr.push(r);
      map.set(d, arr);
    });
    return Array.from(map.entries()).sort(([a], [b]) =>
      desc ? b.localeCompare(a) : a.localeCompare(b),
    );
  };

  const dateHeader = (d: string) => {
    const dt = new Date(`${d}T00:00:00`);
    const w = ['日', '月', '火', '水', '木', '金', '土'][dt.getDay()];
    return `${dt.getMonth() + 1}/${dt.getDate()}(${w})`;
  };

  // 席が解放された終端（キャンセル/却下）は席情報を出さない（E1 と整合）
  const isSeatReleased = (status: ReservationStatus) =>
    status === 'CANCELLED' || status === 'REJECTED';

  /**
   * 割当席タイプ・消化卓数のチップ（E4）。
   * 割当あり → 「{seatTypeName} ×{units}卓」。未割当 → 淡色「未割当」。
   * キャンセル/却下では席情報を出さない。BE 未対応（seatTypeName undefined）では何も出さない。
   */
  const renderSeatChip = (r: ReservationListItem) => {
    if (isSeatReleased(r.status)) return null;
    // seatTypeName フィールド自体が無い（旧BE）なら表示しない。null は「未割当」として扱う。
    if (r.seatTypeName === undefined) return null;
    if (r.seatTypeName === null) {
      return (
        <Chip
          size="small"
          variant="outlined"
          label="未割当"
          sx={{ color: 'text.disabled', borderColor: 'divider' }}
        />
      );
    }
    return (
      <Chip
        size="small"
        variant="outlined"
        color="primary"
        label={`${r.seatTypeName} ×${r.units ?? 1}卓`}
      />
    );
  };

  /** スマホ用カードリスト（テーブルは狭幅で1文字ずつ縦に折れて読めないため md 未満はカード表示） */
  const renderListCards = (items: ReservationListItem[], isSample: boolean) => (
    <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1 }}>
      {items.map((r) => (
        <Paper
          key={r.id}
          variant="outlined"
          onClick={() => (isSample ? setSampleDetailId(r.id) : openDetail(r.id))}
          sx={{
            p: 1.5,
            cursor: 'pointer',
            ...(r.status === 'REQUESTED' && { backgroundColor: 'rgba(255, 152, 0, 0.10)' }),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap' }}>
              {formatReservationDateTime(r.reservationTime)}
            </Typography>
            <Chip
              size="small"
              label={RESERVATION_STATUS_LABEL[r.status]}
              color={RESERVATION_STATUS_COLOR[r.status]}
              variant={r.status === 'REQUESTED' ? 'filled' : 'outlined'}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                ...(r.anonymized && { color: 'text.disabled' }),
              }}
            >
              {r.anonymized ? ANONYMIZED_NAME_LABEL : `${r.userName} 様`}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              {r.numberOfPeople}名
            </Typography>
            {renderSeatChip(r)}
            {isSample && <SampleChip />}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            component="p"
            sx={{ mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {r.selectedMenuName ? `${r.selectedMenuName}・` : ''}
            {r.reservationNumber}
          </Typography>
        </Paper>
      ))}
    </Box>
  );

  /** 一覧テーブル（実データ/サンプル共通。サンプルは行にバッジを付けて詳細もモックダイアログで開く） */
  const renderListTable = (items: ReservationListItem[], isSample: boolean) => (
    <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' } }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>日時</TableCell>
            <TableCell>お名前</TableCell>
            <TableCell align="right">人数</TableCell>
            <TableCell>席</TableCell>
            <TableCell>コース</TableCell>
            <TableCell>ステータス</TableCell>
            <TableCell>予約番号</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((r) => (
            <TableRow
              key={r.id}
              hover
              onClick={() => (isSample ? setSampleDetailId(r.id) : openDetail(r.id))}
              sx={{
                cursor: 'pointer',
                // 承認待ち（要対応）は行ごと強調する
                ...(r.status === 'REQUESTED' && {
                  backgroundColor: 'rgba(255, 152, 0, 0.10)',
                }),
              }}
            >
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {formatReservationDateTime(r.reservationTime)}
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {r.anonymized ? (
                    <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                      {ANONYMIZED_NAME_LABEL}
                    </Typography>
                  ) : (
                    r.userName
                  )}
                  {isSample && <SampleChip />}
                </Box>
              </TableCell>
              <TableCell align="right">{r.numberOfPeople}名</TableCell>
              <TableCell>{renderSeatChip(r) ?? '-'}</TableCell>
              <TableCell>{r.selectedMenuName ?? '-'}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={RESERVATION_STATUS_LABEL[r.status]}
                  color={RESERVATION_STATUS_COLOR[r.status]}
                  variant={r.status === 'REQUESTED' ? 'filled' : 'outlined'}
                />
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.reservationNumber}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  /** サンプル表示中の共通バナー */
  const sampleBanner = (
    <Alert severity="info" sx={{ mb: 2 }} onClose={() => setShowSample(false)}>
      サンプル予約を表示しています（実際の予約ではありません。操作はできません）
    </Alert>
  );

  /** 空状態 + サンプル表示ボタン */
  const emptyState = (message: string) => (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <Typography color="text.secondary">{message}</Typography>
      <Button
        size="small"
        startIcon={<VisibilityIcon />}
        sx={{ mt: 1 }}
        onClick={() => setShowSample(true)}
      >
        サンプル予約を表示
      </Button>
    </Box>
  );

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ mt: 3, mb: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5">予約管理</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/${store_id}/manage/reservation/new`)}
            >
              予約を登録
            </Button>
          </Box>
        </Box>

        {/* 日付ナビ + 更新 + 表示切替 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            mb: 2,
          }}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={range}
            // ダッシュボードは粒度(時間/日/月)を内包し全期間で使えるため、range は無効化する。
            disabled={view === 'dashboard'}
            onChange={(_, v) => {
              if (!v) return;
              setRange(v);
              // 台帳は日単位のため、今後すべて/過去では台帳を選べない → リストへ戻す。
              // ダッシュボードは range に依存しないためそのまま維持する。
              if (v !== 'day' && view === 'timeline') setView('list');
            }}
          >
            <ToggleButton value="past" aria-label="過去（14日）表示">過去(14日)</ToggleButton>
            <ToggleButton value="day" aria-label="日別表示">日別</ToggleButton>
            <ToggleButton value="upcoming" aria-label="今後すべて表示">今後すべて</ToggleButton>
          </ToggleButtonGroup>
          {range === 'day' && view !== 'dashboard' && (
            <>
              <IconButton aria-label="前日" onClick={() => moveDate(-1)} size="small">
                <ChevronLeftIcon />
              </IconButton>
              <TextField
                type="date"
                size="small"
                value={date}
                onChange={(e) => e.target.value && setDate(e.target.value)}
              />
              <IconButton aria-label="翌日" onClick={() => moveDate(1)} size="small">
                <ChevronRightIcon />
              </IconButton>
              <Button size="small" onClick={() => setDate(toDateString(new Date()))}>
                今日
              </Button>
            </>
          )}
          <IconButton aria-label="更新" onClick={handleRefresh} color="primary">
            <RefreshIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          {/* リスト / 台帳 / ダッシュボード の3値排他タブ（統合契約 タスク2）。
              台帳は日単位のため range==='day' のときのみ選択肢に出す（ダッシュボードは全期間可）。 */}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, v) => v && setView(v)}
          >
            <ToggleButton value="list" aria-label="リスト表示">
              <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />
              リスト
            </ToggleButton>
            {range === 'day' && (
              <ToggleButton value="timeline" aria-label="台帳表示">
                <ViewTimelineIcon fontSize="small" sx={{ mr: 0.5 }} />
                台帳
              </ToggleButton>
            )}
            <ToggleButton value="dashboard" aria-label="ダッシュボード表示">
              <DashboardIcon fontSize="small" sx={{ mr: 0.5 }} />
              ダッシュボード
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {view === 'list' && (
          <>
            {/* ステータスフィルタ + 検索 */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
              <Chip
                label="すべて"
                color={statusFilter === null ? 'primary' : 'default'}
                variant={statusFilter === null ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter(null)}
              />
              {STATUS_FILTERS.map((s) => (
                <Chip
                  key={s}
                  label={RESERVATION_STATUS_LABEL[s]}
                  color={statusFilter === s ? RESERVATION_STATUS_COLOR[s] : 'default'}
                  variant={statusFilter === s ? 'filled' : 'outlined'}
                  onClick={() => setStatusFilter(statusFilter === s ? null : s)}
                />
              ))}
              {range === 'upcoming' && (
                <TextField
                  select
                  size="small"
                  label="コース"
                  value={courseFilter ?? '__all__'}
                  onChange={(e) => setCourseFilter(e.target.value === '__all__' ? null : e.target.value)}
                  sx={{ minWidth: 180 }}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="__all__">すべて</option>
                  {courseOptions.map((name) => (
                    <option key={name || '__none__'} value={name}>
                      {name || 'コースなし'}
                    </option>
                  ))}
                </TextField>
              )}
              <Box sx={{ flexGrow: 1 }} />
              <TextField
                size="small"
                placeholder="氏名・電話・予約番号で検索"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                onBlur={() => {
                  // 入力を消したら検索を解除して日付表示へ戻す
                  if (!searchInput.trim() && query) setQuery('');
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="検索" onClick={handleSearch}>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 240 }}
              />
            </Box>

            {query && (
              <Alert severity="info" sx={{ mb: 2 }} onClose={() => { setQuery(''); setSearchInput(''); }}>
                「{query}」の検索結果を全期間から表示しています（閉じると日付表示に戻ります）
              </Alert>
            )}

            {!query && range === 'past' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                過去14日分の予約を新しい順に表示しています（忘れ物などのご連絡用）。個人情報保護のため、14日より前の予約は表示されません。
              </Alert>
            )}

            {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

            {listLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : list.length === 0 ? (
              query ? (
                <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                  該当する予約がありません
                </Typography>
              ) : showSample ? (
                <>
                  {sampleBanner}
                  {renderListTable(buildSampleList(date), true)}
                  {renderListCards(buildSampleList(date), true)}
                </>
              ) : (
                emptyState(
                  range === 'upcoming'
                    ? '今後の予約はありません'
                    : range === 'past'
                      ? '過去14日以内の予約はありません'
                      : 'この日の予約はありません',
                )
              )
            ) : visibleList.length === 0 ? (
              <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                選択中のコースに該当する予約がありません
              </Typography>
            ) : range !== 'day' ? (
              groupByDate(visibleList, range === 'past').map(([d, items]) => (
                <Box key={d} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {dateHeader(d)}
                  </Typography>
                  {renderListTable(items, false)}
                  {renderListCards(items, false)}
                </Box>
              ))
            ) : (
              <>
                {renderListTable(visibleList, false)}
                {renderListCards(visibleList, false)}
              </>
            )}
          </>
        )}

        {view === 'timeline' && (
          <>
            {timelineError && <Alert severity="error" sx={{ mb: 2 }}>{timelineError}</Alert>}
            {timelineLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : timeline ? (
              timeline.entries.length === 0 ? (
                showSample ? (
                  <>
                    {sampleBanner}
                    <TimelineBoard
                      timeline={buildSampleTimeline(date)}
                      onEntryClick={(id) => setSampleDetailId(id)}
                    />
                  </>
                ) : (
                  emptyState('この日の予約はありません')
                )
              ) : (
                <TimelineBoard timeline={timeline} onEntryClick={openDetail} />
              )
            ) : null}
          </>
        )}

        {/* ダッシュボード（統合契約 タスク2）。粒度トグル（時間別/日別/月別）を内包し range に依存しない。
            全ステータスフィルタで表示する。 */}
        {view === 'dashboard' && store_id && (
          <ReservationDashboard
            storeId={store_id}
            statusFilter={statusFilter}
            baseDate={date}
          />
        )}

        {/* サンプル予約の詳細レイアウト確認ダイアログ */}
        <SampleReservationDetailDialog
          detail={sampleDetailId ? buildSampleDetail(sampleDetailId, date) : null}
          onClose={() => setSampleDetailId(null)}
        />
      </Container>
    </>
  );
};

export default ReservationManagement;
